// client/src/hooks/domain/useConvoyRadar.ts
//
// Squad Radar — WebSocket client per il Chapter Live Ride (Ott 2026).
//
// Consuma il server /ws/live-ride (liveRideServer.ts) con il protocollo:
//   → { type: "auth", token }
//   ← { type: "auth_ok", userId }
//   → { type: "join", sessionId }
//   ← { type: "session_status", status, leadUserId, sweeperUserId, riders }
//   ← { type: "positions", riders }
//   ← { type: "rider_joined" | "rider_left" | "rider_offline", userId, reason? }
//   ← { type: "alert", alertType, message, riderId, severity, gapM? }
//   → { type: "position", lat, lng, speed, heading }  (se sharePosition)
//   → { type: "heartbeat" }                            (ogni 15s)
//
// Riusa i pattern del hook generico useWebSocket (auth su open, backoff di
// riconnessione, stop in background per risparmio batteria) ma il protocollo
// del Live Ride è dedicato: l'URL è /ws/live-ride e i messaggi differiscono.
//
// La condivisione della posizione è OPZIONALE e controllata dall'utente
// (sharePosition): senza di essa il radar resta in sola lettura.

import { useCallback, useEffect, useRef, useState } from "react";
import useAuthStore from "@/store/useAuthStore";
import { resolveApiBaseUrl } from "@/utils/apiBase";

const WS_BASE: string = resolveApiBaseUrl().replace(/^http/, "ws");
const LIVE_RIDE_WS_URL = `${WS_BASE}/ws/live-ride`;

const HEARTBEAT_INTERVAL_MS = 15_000;
const POSITION_THROTTLE_MS = 5_000;
const MAX_RECONNECT_ATTEMPTS = 8;
const MAX_ALERTS_KEPT = 5;

// ─── Tipi (allineati a liveRideServer.ts) ───────────────────────────────────

export type ConvoyRole = "lead" | "sweeper" | "rider";

export interface ConvoyRider {
  userId: string;
  lat: number;
  lng: number;
  speed: number;
  heading: number;
  batteryPct?: number;
  role: ConvoyRole;
  online: boolean;
  /** ISO string o null se nessun fix ricevuto in sessione */
  lastUpdate: string | null;
  lastHeartbeat?: string;
}

export interface ConvoyAlert {
  id: string;
  alertType: string;
  message: string;
  riderId?: string;
  severity?: string;
  gapM?: number;
  receivedAt: number;
}

interface WsRiderPayload {
  userId: string;
  lat: number;
  lng: number;
  speed?: number;
  heading?: number;
  batteryPct?: number;
  role?: ConvoyRole;
  online?: boolean;
  lastUpdate?: string | null;
  lastHeartbeat?: string;
}

let alertSeq = 0;

function normalizeRiders(raw: unknown): ConvoyRider[] {
  if (!Array.isArray(raw)) return [];
  const byId = new Map<string, ConvoyRider>();
  for (const item of raw as WsRiderPayload[]) {
    if (!item || typeof item.userId !== "string") continue;
    byId.set(item.userId, {
      userId: item.userId,
      lat: Number(item.lat) || 0,
      lng: Number(item.lng) || 0,
      speed: Number(item.speed) || 0,
      heading: Number(item.heading) || 0,
      batteryPct: item.batteryPct,
      role: item.role === "lead" || item.role === "sweeper" ? item.role : "rider",
      online: item.online !== false,
      lastUpdate: item.lastUpdate ?? null,
      lastHeartbeat: item.lastHeartbeat,
    });
  }
  return Array.from(byId.values());
}

export interface UseConvoyRadarOptions {
  /** Sessione live ride a cui unirsi; null → nessuna connessione */
  sessionId: string | null;
  /** Abilita la connessione (default true) */
  enabled?: boolean;
  /** Trasmetti la mia posizione GPS al convoglio (opt-in utente) */
  sharePosition?: boolean;
  /** Chiamata quando la condivisione GPS fallisce (permessi/errore) */
  onPositionError?: (message: string) => void;
}

export interface UseConvoyRadarResult {
  connected: boolean;
  joined: boolean;
  riders: ConvoyRider[];
  sessionStatus: string | null;
  leadUserId: string | null;
  sweeperUserId: string | null;
  alerts: ConvoyAlert[];
  error: string | null;
  /** True quando un fix GPS locale è stato inviato di recente */
  sendingPosition: boolean;
  dismissAlert: (id: string) => void;
}

/**
 * Connessione real-time al convoglio (Squad Radar).
 * Riutilizzabile sia dalla pagina evento sia da una eventuale vista full-screen.
 */
export function useConvoyRadar({
  sessionId,
  enabled = true,
  sharePosition = false,
  onPositionError,
}: UseConvoyRadarOptions): UseConvoyRadarResult {
  const [connected, setConnected] = useState(false);
  const [joined, setJoined] = useState(false);
  const [riders, setRiders] = useState<ConvoyRider[]>([]);
  const [sessionStatus, setSessionStatus] = useState<string | null>(null);
  const [leadUserId, setLeadUserId] = useState<string | null>(null);
  const [sweeperUserId, setSweeperUserId] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<ConvoyAlert[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sendingPosition, setSendingPosition] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const isMountedRef = useRef(true);
  const sessionIdRef = useRef<string | null>(sessionId);
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const joinedRef = useRef(false);
  const onPositionErrorRef = useRef(onPositionError);
  onPositionErrorRef.current = onPositionError;

  sessionIdRef.current = sessionId;

  const pushAlert = useCallback((alert: Omit<ConvoyAlert, "id" | "receivedAt">) => {
    setAlerts((prev) =>
      [
        { ...alert, id: `a${++alertSeq}`, receivedAt: Date.now() },
        ...prev,
      ].slice(0, MAX_ALERTS_KEPT),
    );
  }, []);

  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const send = useCallback((msg: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }, []);

  // ── Connessione ────────────────────────────────────────────────────────────
  const connect = useCallback(() => {
    if (!enabled || !sessionIdRef.current) return;
    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) return;

    const token = useAuthStore.getState().accessToken;
    if (!token) return; // si ricollega al login (effect sotto)

    try {
      const ws = new WebSocket(LIVE_RIDE_WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttempts.current = 0;
        joinedRef.current = false;
        setConnected(true);
        setError(null);
        ws.send(JSON.stringify({ type: "auth", token }));
      };

      ws.onmessage = (event) => {
        let msg: any;
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }

        switch (msg?.type) {
          case "auth_ok": {
            const sid = sessionIdRef.current;
            if (sid) ws.send(JSON.stringify({ type: "join", sessionId: sid }));
            break;
          }
          case "session_status": {
            joinedRef.current = true;
            setJoined(true);
            if (typeof msg.status === "string") setSessionStatus(msg.status);
            setLeadUserId(typeof msg.leadUserId === "string" ? msg.leadUserId : null);
            setSweeperUserId(
              typeof msg.sweeperUserId === "string" && msg.sweeperUserId
                ? msg.sweeperUserId
                : null,
            );
            if (msg.riders) setRiders(normalizeRiders(msg.riders));
            break;
          }
          case "positions": {
            setRiders(normalizeRiders(msg.riders));
            break;
          }
          case "rider_joined": {
            // Il rider apparirà con la prima posizione; mostra subito "in attesa"
            if (typeof msg.userId === "string") {
              setRiders((prev) =>
                prev.some((r) => r.userId === msg.userId)
                  ? prev
                  : [
                      ...prev,
                      {
                        userId: msg.userId,
                        lat: 0,
                        lng: 0,
                        speed: 0,
                        heading: 0,
                        role: msg.role === "lead" || msg.role === "sweeper" ? msg.role : "rider",
                        online: true,
                        lastUpdate: null,
                      },
                    ],
              );
            }
            break;
          }
          case "rider_left": {
            if (typeof msg.userId === "string") {
              setRiders((prev) => prev.filter((r) => r.userId !== msg.userId));
            }
            break;
          }
          case "rider_offline": {
            if (typeof msg.userId === "string") {
              setRiders((prev) =>
                prev.map((r) =>
                  r.userId === msg.userId ? { ...r, online: false } : r,
                ),
              );
            }
            break;
          }
          case "alert": {
            pushAlert({
              alertType: String(msg.alertType || "generic"),
              message: String(msg.message || "Avviso dal convoglio"),
              riderId: typeof msg.riderId === "string" ? msg.riderId : undefined,
              severity: typeof msg.severity === "string" ? msg.severity : undefined,
              gapM: typeof msg.gapM === "number" ? msg.gapM : undefined,
            });
            break;
          }
          case "error": {
            setError(String(msg.message || "Errore di connessione al radar"));
            break;
          }
        }
      };

      ws.onclose = () => {
        setConnected(false);
        setJoined(false);
        joinedRef.current = false;
        if (
          isMountedRef.current &&
          enabled &&
          sessionIdRef.current &&
          reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS
        ) {
          reconnectAttempts.current++;
          const delay = Math.min(3000 * Math.pow(2, reconnectAttempts.current - 1), 60000);
          reconnectTimer.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        // onerror è sempre seguito da onclose: la logica di retry è lì.
      };
    } catch {
      if (isMountedRef.current) {
        reconnectAttempts.current++;
        reconnectTimer.current = setTimeout(connect, 3000);
      }
    }
  }, [enabled, pushAlert]);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    const ws = wsRef.current;
    if (ws) {
      if (ws.readyState === WebSocket.OPEN) {
        if (joinedRef.current) {
          try {
            ws.send(JSON.stringify({ type: "leave" }));
          } catch {
            /* best effort */
          }
        }
        ws.close();
      } else if (ws.readyState === WebSocket.CONNECTING) {
        ws.onopen = () => ws.close();
      }
      wsRef.current = null;
    }
  }, []);

  // Reconnect quando cambia la sessione o l'enabled
  useEffect(() => {
    isMountedRef.current = true;
    reconnectAttempts.current = 0;
    if (sessionId && enabled) {
      connect();
    } else {
      disconnect();
      setRiders([]);
      setJoined(false);
    }
    return () => {
      isMountedRef.current = false;
      disconnect();
    };
  }, [sessionId, enabled, connect, disconnect]);

  // Riconnessione automatica al login
  useEffect(() => {
    const unsub = useAuthStore.subscribe((state, prev) => {
      if (state.accessToken && !prev.accessToken && sessionIdRef.current && enabled) {
        reconnectAttempts.current = 0;
        connect();
      }
    });
    return unsub;
  }, [connect, enabled]);

  // Background: chiudi la socket (risparmio batteria), riconnetti al ritorno
  useEffect(() => {
    const onVisibility = () => {
      if (!isMountedRef.current) return;
      if (document.visibilityState === "visible") {
        if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
          reconnectAttempts.current = 0;
          connect();
        }
      } else {
        disconnect();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [connect, disconnect]);

  // Heartbeat periodico (il server considera offline dopo 45s)
  useEffect(() => {
    if (!connected || !joined) {
      if (heartbeatTimer.current) {
        clearInterval(heartbeatTimer.current);
        heartbeatTimer.current = null;
      }
      return;
    }
    heartbeatTimer.current = setInterval(() => {
      send({ type: "heartbeat" });
    }, HEARTBEAT_INTERVAL_MS);
    return () => {
      if (heartbeatTimer.current) {
        clearInterval(heartbeatTimer.current);
        heartbeatTimer.current = null;
      }
    };
  }, [connected, joined, send]);

  // ── Streaming posizione (opt-in) ───────────────────────────────────────────
  useEffect(() => {
    if (!sharePosition || !connected || !joined || !sessionId) {
      setSendingPosition(false);
      return;
    }
    if (!("geolocation" in navigator)) {
      onPositionErrorRef.current?.("GPS non disponibile su questo dispositivo");
      return;
    }

    let lastSentAt = 0;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastSentAt < POSITION_THROTTLE_MS) return;
        lastSentAt = now;

        const { latitude, longitude, speed, heading } = pos.coords;
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

        send({
          type: "position",
          lat: latitude,
          lng: longitude,
          // Il protocollo Live Ride usa km/h (schema Zod 0..400)
          speed: speed != null && Number.isFinite(speed) ? Math.min(400, Math.max(0, speed * 3.6)) : 0,
          heading: heading != null && Number.isFinite(heading) ? Math.min(360, Math.max(0, heading)) : 0,
        });
        setSendingPosition(true);
      },
      (err) => {
        setSendingPosition(false);
        onPositionErrorRef.current?.(
          err?.code === 1
            ? "Permesso di localizzazione negato"
            : "Segnale GPS non disponibile",
        );
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 },
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
      setSendingPosition(false);
    };
  }, [sharePosition, connected, joined, sessionId, send]);

  return {
    connected,
    joined,
    riders,
    sessionStatus,
    leadUserId,
    sweeperUserId,
    alerts,
    error,
    sendingPosition,
    dismissAlert,
  };
}
